import { createAsyncThunk } from '@/redux/createAsyncThunk';
import { getClientIpAddress } from '@/redux/modules/browser';
import { getDeployment } from '@/redux/modules/config';
import { GlobalState } from '@/redux/store';

import { getSearchSuggestions } from './searchSuggestions.api';
import { MIN_SUGGESTION_QUERY_LENGTH, SearchSuggestion } from './searchSuggestions.types';

type FetchSearchSuggestionsOptions = {
    term: string;
};

/**
 * Skips the dispatch entirely when the trimmed term is 1-2 characters. Allows empty terms
 * through so the thunk body can resolve with `[]` (which clears the relevant slice via the
 * fulfilled reducer), and allows 3+ character terms so they reach the API.
 */
const searchSuggestionsCondition = ({ term }: FetchSearchSuggestionsOptions) => {
    const length = term.trim().length;
    return length === 0 || length >= MIN_SUGGESTION_QUERY_LENGTH;
};

const fetchSuggestionsPayload = async (
    { term }: FetchSearchSuggestionsOptions,
    state: GlobalState
): Promise<SearchSuggestion[]> => {
    if (term.trim().length === 0) {
        return [];
    }
    const response = await getSearchSuggestions({
        deployment: getDeployment(state),
        ipAddress: getClientIpAddress(state),
        term,
    });
    return response.payload ?? [];
};

/**
 * Drives the type-ahead dropdown (primary suggestions). The `fulfilled` reducer writes the
 * payload to `state.searchSuggestions.suggestions`.
 */
export const fetchSearchSuggestions = createAsyncThunk<SearchSuggestion[], FetchSearchSuggestionsOptions>(
    'la/ui/searchSuggestions/fetchSearchSuggestions',
    (options, { getState }) => fetchSuggestionsPayload(options, getState()),
    { condition: searchSuggestionsCondition }
);

/**
 * Drives the SSR search-results page's auction-house suggestions. The `fulfilled` reducer writes
 * the payload to `state.searchSuggestions.secondarySuggestions`, keeping it separate from the
 * type-ahead dropdown's data so the two flows don't clobber each other.
 */
export const fetchSecondarySearchSuggestions = createAsyncThunk<SearchSuggestion[], FetchSearchSuggestionsOptions>(
    'la/ui/searchSuggestions/fetchSecondarySearchSuggestions',
    (options, { getState }) => fetchSuggestionsPayload(options, getState()),
    { condition: searchSuggestionsCondition }
);
